import { useState, useMemo, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { useDashboardData } from "@/hooks/useDashboardData";
import { useDashboardActions } from "@/hooks/useDashboardActions";
import { useAdminHealth } from "@/hooks/useAdminHealth";
import { useOperatorScope } from "@/hooks/useOperatorScope";
import { computeKpis } from "@/lib/kpis";
import { can } from "@/lib/rbac";
import Navbar from "@/components/Navbar";
import KpiCards from "@/components/KpiCards";
import AnalyticsCharts from "@/components/AnalyticsCharts";
import GanttTimeline from "@/components/GanttTimeline";
import ProductionTable from "@/components/ProductionTable";
import OrderFormModal from "@/components/OrderFormModal";
import MachinesPanel from "@/components/MachinesPanel";
import UserManagement from "@/components/UserManagement";
import InventoryLog from "@/components/bom/InventoryLog";
import PlantSettingsDialog from "@/components/PlantSettingsDialog";
import DigestSettingsDialog from "@/components/DigestSettingsDialog";
import StageLogDialog from "@/components/stages/StageLogDialog";
import StageHistoryDrawer from "@/components/stages/StageHistoryDrawer";
import VendorOverdueBanner from "@/components/osp/VendorOverdueBanner";
import BomPanel from "@/components/bom/BomPanel";
import WorkOrdersPanel from "@/components/workorders/WorkOrdersPanel";
import MaterialsSheet from "@/components/bom/MaterialsSheet";
import PurchaseSuggestionsDialog from "@/components/bom/PurchaseSuggestionsDialog";
import PurchaseOrdersPanel from "@/components/purchasing/PurchaseOrdersPanel";
import SuppliersPanel from "@/components/purchasing/SuppliersPanel";
import CustomersPanel from "@/components/customers/CustomersPanel";
import SalesPanel from "@/components/sales/SalesPanel";
import OspPanel from "@/components/osp/OspPanel";
import ShipToVendorDialog from "@/components/osp/ShipToVendorDialog";
import ReceiveFromVendorDialog from "@/components/osp/ReceiveFromVendorDialog";
import GlobalInventory from "@/components/inventory/GlobalInventory";
import SystemOverview from "@/components/SystemOverview";
import ProductionPlanning from "@/components/ProductionPlanning";
import MaterialShortageDialog from "@/components/MaterialShortageDialog";
import DashboardHeader from "@/components/dashboard/DashboardHeader";
import ApprovalsInbox from "@/components/approvals/ApprovalsInbox";
import MyApprovals from "@/components/approvals/MyApprovals";
import OperatorUpdates from "@/components/approvals/OperatorUpdates";
import TrustedDevices from "@/components/admin/TrustedDevices";
import AppSidebar from "@/components/dashboard/AppSidebar";
import AlertBanners from "@/components/dashboard/AlertBanners";
import ShortageTile from "@/components/dashboard/ShortageTile";
import OperatorScopeBanner from "@/components/dashboard/OperatorScopeBanner";
import SuggestionDialog from "@/components/dashboard/SuggestionDialog";
import ConfirmDialog from "@/components/dashboard/ConfirmDialog";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Menu } from "lucide-react";
import { useT } from "@/lib/i18n";
import { isOverdue } from "@/lib/batches";
import { StageFilterChips, stageFilterApply } from "@/components/StageFilterChips";

export default function Dashboard() {
  const { user } = useAuth();
  const t = useT();
  const role = user.role;
  const canManage = can.manageOrders(role);
  const isAdmin = role === "admin";
  const {
    facility, orders, setOrders, machines, users, kpis, facilities, downtimes,
    bomItems, shortages, weekShortages, purchaseSuggestions, poSummary, poVersion, suppliers, customers, salesOrders, incoming, ospVendors, gatePasses, activeDrags, workOrders,
    loadCore, loadUsers, reloadFacilities, realtime, handleDragPresence, lockoutTick, deviceTick,
  } = useDashboardData(role, user.name, user.id);
  const { lockoutCount, pendingDevices, lastBackupAt, refresh: refreshLockouts } = useAdminHealth(role, lockoutTick, deviceTick);

  const [tab, setTab] = useState("dashboard");
  const [sidebarPinned, setSidebarPinned] = useState(() => localStorage.getItem("sidebarPinned") === "1");
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const toggleSidebarPin = () => setSidebarPinned((p) => {
    localStorage.setItem("sidebarPinned", p ? "0" : "1");
    return !p;
  });
  useEffect(() => {
    const onNav = (e) => e.detail && setTab(e.detail);
    window.addEventListener("proplan:navigate", onNav);
    return () => window.removeEventListener("proplan:navigate", onNav);
  }, []);
  const [ganttFocus, setGanttFocus] = useState(null);
  const [orderModal, setOrderModal] = useState({ open: false, order: null });
  const [confirm, setConfirm] = useState(null); // { title, desc, action }
  const [filterDelayed, setFilterDelayed] = useState(false);
  const [suggestion, setSuggestion] = useState(null); // { order, data, loading }
  const [plantSettingsOpen, setPlantSettingsOpen] = useState(false);
  const [digestOpen, setDigestOpen] = useState(false);
  const [materialsOrder, setMaterialsOrder] = useState(null);
  const [purchaseOpen, setPurchaseOpen] = useState(false);
  const [shortageFor, setShortageFor] = useState(null);
  // Stage-Gate action popup: { type: "log" | "ship" | "receive", order, idx }
  const [stageAction, setStageAction] = useState(null);
  const allStageOrders = useMemo(() => [...orders, ...workOrders], [orders, workOrders]);
  // Work orders that are Overdue (past due date) or Running Late (Delayed) — same standard as Sales "Confirmed and Overdue".
  const lateWorkOrders = useMemo(
    () => workOrders.filter((w) => !["Completed", "Cancelled"].includes(w.status) && (isOverdue(w) || w.status === "Delayed")).length,
    [workOrders],
  );
  const stageCtx = useMemo(
    () => (stageAction ? { order: allStageOrders.find((o) => o.id === stageAction.order.id) || stageAction.order, idx: stageAction.idx } : null),
    [stageAction, allStageOrders],
  );
  const openStage = (type) => (order, idx) => setStageAction({ type, order, idx });
  const [historyOrder, setHistoryOrder] = useState(null);
  const liveHistoryOrder = historyOrder ? allStageOrders.find((o) => o.id === historyOrder.id) || historyOrder : null;
  const activeSuppliers = useMemo(() => suppliers.filter((s) => s.active !== false), [suppliers]);
  const openPoItemIds = useMemo(() => new Set(poSummary.open_item_ids || []), [poSummary.open_item_ids]);
  const operatorUsers = useMemo(() => users.filter((u) => u.role === "operator"), [users]);

  const {
    saveOrder, rescheduleOrder, suggestFix, applySuggestion, deleteOrder,
    createMachine, updateMachine, deleteMachine, logDowntime, deleteDowntime,
    createUser, updateUser, deleteUser, savePlantSettings,
    saveBomItem, adjustStock, deleteBomItem, archiveBomItem, approveBomItem, rejectBomItem,
    createPO, createBulkPO, reorderPO, orderAllShort, receivePO, editPO, cancelPO, approvePO, rejectPO, resubmitPO, bulkApprove, updateSupplier, createSupplier, createCustomer, updateCustomer,
    approveSupplier, rejectSupplier, resubmitSupplier, approveCustomer, rejectCustomer, resubmitCustomer,
    createSalesOrder, confirmSalesOrder, fulfillSalesOrder, cancelSalesOrder, updateSalesOrder, produceToStock, raisePosForSalesOrder, createWorkOrderForSalesOrder,
    logStage, shipStage, receiveStage, receiveGatePass, createOspVendor, createWorkOrder, editWorkOrder, deleteWorkOrder, applyBomRefresh, acknowledgeBom, clearAllData, seedDemoData,
  } = useDashboardActions({
    facility, orderModal, suggestion, loadCore, loadUsers, reloadFacilities,
    setOrders, setSuggestion, setConfirm,
  });

  const activeFacility = facilities.find((f) => f.code === facility);
  // Operators see only their assigned lines + the jobs on them; planners/admins see everything.
  const scope = useOperatorScope(user, machines, orders, downtimes, gatePasses);
  const viewKpis = useMemo(
    () => (scope.scoped ? computeKpis(scope.orders, scope.machines, activeFacility?.shift_hours) : kpis),
    [scope, kpis, activeFacility?.shift_hours],
  );
  const openNewOrder = () => setOrderModal({ open: true, order: null });
  const [highlightOrder, setHighlightOrder] = useState(null);
  const [schStageFilter, setSchStageFilter] = useState("all");
  const onOpenOrder = (code) => {
    if (!code) return;
    setFilterDelayed(false);
    setSchStageFilter("all");
    setHighlightOrder(code);
    setTab("schedule");
  };
  const submitOrder = async (payload) => {
    const wasCreate = !orderModal.order;
    const res = await saveOrder(payload);
    if (wasCreate && res?.line_id) { setGanttFocus(res.line_id); setTab("schedule"); }
  };
  const proceedFromShortage = () => {
    setOrderModal({ open: true, order: null, prefill: { product_name: shortageFor.product_name, bom_item_id: shortageFor.item_id, target_quantity: shortageFor.quantity } });
    setShortageFor(null);
  };

  const tabBadges = useMemo(() => ({
    suppliers: suppliers.filter((s) => s.status === "Pending Approval").length,
    customers: customers.filter((c) => c.status === "Pending Approval").length,
    bom: bomItems.filter((b) => b.approval_status === "Pending Approval").length,
    approvals: (poSummary.pending_approval_count || 0) + suppliers.filter((s) => s.status === "Pending Approval").length + customers.filter((c) => c.status === "Pending Approval").length + bomItems.filter((b) => b.approval_status === "Pending Approval").length,
    myPending: suppliers.filter((s) => s.status === "Pending Approval" && s.created_by === user.username).length + customers.filter((c) => c.status === "Pending Approval" && c.created_by === user.username).length + bomItems.filter((b) => b.approval_status === "Pending Approval" && (b.created_by === user.username || b.updated_by === user.username)).length,
    myRejected: suppliers.filter((s) => s.status === "Rejected" && s.created_by === user.username).length + customers.filter((c) => c.status === "Rejected" && c.created_by === user.username).length + bomItems.filter((b) => b.approval_status === "Rejected" && (b.created_by === user.username || b.updated_by === user.username)).length,
    updates: scope.orders.filter((o) => o.ack_required && !(o.acknowledged_by || []).some((a) => a.by === user.username)).length,
  }), [suppliers, customers, bomItems, poSummary.pending_approval_count, scope.orders, user.username]);
  const sidebarCounts = useMemo(() => ({ pendingApprovals: poSummary.pending_approval_count, lockoutCount, pendingDevices, lateWorkOrders }), [poSummary.pending_approval_count, lockoutCount, pendingDevices, lateWorkOrders]);

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar realtime={realtime} />
      <div className="flex items-start">
        <AppSidebar role={role} tab={tab} onTab={setTab} pinned={sidebarPinned} onTogglePinned={toggleSidebarPin} tabBadges={tabBadges} counts={sidebarCounts} />
        <main className="flex-1 min-w-0 max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        <button
          type="button"
          onClick={() => setMobileNavOpen(true)}
          data-testid="mobile-nav-button"
          className="lg:hidden inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100"
        >
          <Menu className="h-4 w-4" />{t("nav.menu")}
        </button>
        <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
          <SheetContent side="left" className="w-72 p-0" data-testid="mobile-nav-sheet">
            <SheetTitle className="sr-only">{t("nav.menu")}</SheetTitle>
            <AppSidebar mobile role={role} tab={tab} onTab={(v) => { setTab(v); setMobileNavOpen(false); }} tabBadges={tabBadges} counts={sidebarCounts} />
          </SheetContent>
        </Sheet>
        <DashboardHeader facility={facility} role={role} purchaseCount={purchaseSuggestions.length}
          onPlantSettings={() => setPlantSettingsOpen(true)} onDigest={() => setDigestOpen(true)} onPurchase={() => setPurchaseOpen(true)} onNewOrder={openNewOrder} />
        <OperatorScopeBanner scope={scope} />

        <Tabs value={tab} onValueChange={setTab}>

          <TabsContent value="dashboard" className="space-y-6 mt-6">
            <AlertBanners canManage={canManage} isAdmin={isAdmin} gatePasses={scope.gatePasses} bomItems={bomItems} poSummary={poSummary}
              masterApprovals={{ suppliers: suppliers.filter((s) => s.status === "Pending Approval").length, customers: customers.filter((c) => c.status === "Pending Approval").length, bom: bomItems.filter((b) => b.approval_status === "Pending Approval").length }}
              purchaseSuggestions={purchaseSuggestions} lastBackupAt={lastBackupAt} onTab={setTab} onPurchase={() => setPurchaseOpen(true)} />
            <KpiCards kpis={viewKpis} onFilterDelayed={() => { setFilterDelayed(true); setTab("schedule"); }} />
            {canManage && <ShortageTile data={weekShortages} onPurchase={() => setPurchaseOpen(true)} />}
            <AnalyticsCharts kpis={viewKpis} />
          </TabsContent>

          {isAdmin && (
            <TabsContent value="approvals" className="mt-6">
              <ApprovalsInbox facility={facility} orders={allStageOrders} bomItems={bomItems} suppliers={suppliers} customers={customers} poVersion={poVersion}
                onApprovePO={approvePO} onRejectPO={rejectPO} onApproveBom={approveBomItem} onRejectBom={rejectBomItem}
                onApproveSupplier={approveSupplier} onRejectSupplier={rejectSupplier} onApproveCustomer={approveCustomer} onRejectCustomer={rejectCustomer}
                onApplyBomRefresh={applyBomRefresh} onNavigate={setTab} />
            </TabsContent>
          )}

          {role === "operator" && (
            <TabsContent value="updates" className="mt-6">
              <OperatorUpdates orders={scope.orders} currentUsername={user.username} onAcknowledge={acknowledgeBom} onNavigate={setTab} />
            </TabsContent>
          )}

          {role === "planner" && (
            <TabsContent value="my-approvals" className="mt-6">
              <MyApprovals currentUsername={user.username} facility={facility} bomItems={bomItems} suppliers={suppliers} customers={customers} machines={machines} poVersion={poVersion}
                onResubmitSupplier={resubmitSupplier} onResubmitCustomer={resubmitCustomer} onResubmitPO={resubmitPO} onSaveBomItem={saveBomItem} onNavigate={setTab} />
            </TabsContent>
          )}

          <TabsContent value="schedule" className="space-y-6 mt-6">
            <VendorOverdueBanner gatePasses={scope.gatePasses} onReview={canManage ? () => setTab("osp") : undefined} />
            {filterDelayed && (
              <div className="flex items-center gap-2 text-sm">
                <span className="text-red-600 font-medium">Showing delayed orders only</span>
                <button onClick={() => setFilterDelayed(false)} className="text-blue-600 underline text-xs">clear filter</button>
              </div>
            )}
            <StageFilterChips orders={scope.orders} value={schStageFilter} onChange={setSchStageFilter} containerTestid="sch-stage-filters" itemPrefix="sch-filter" />
            <ProductionTable orders={stageFilterApply(scope.orders, schStageFilter)} machines={scope.machines} role={role} filterDelayed={filterDelayed} sortByStatus="schedule" groupBatches spotlightOverdue onOpenOrder={onOpenOrder} isAdmin={isAdmin} currentUsername={user.username} onApplyBomRefresh={applyBomRefresh} onAcknowledgeBom={acknowledgeBom} onEdit={(o) => setOrderModal({ open: true, order: o })} onDelete={deleteOrder} onSuggestFix={suggestFix}
              highlightCode={highlightOrder} onHighlightDone={() => setHighlightOrder(null)}
              onLogStage={openStage("log")} onShipStage={openStage("ship")} onReceiveStage={openStage("receive")} onHistory={setHistoryOrder} />
            <GanttTimeline orders={scope.orders} machines={scope.machines} role={role} onReschedule={rescheduleOrder} activeDrags={activeDrags} onDragPresence={handleDragPresence}
              downtimes={scope.downtimes} workingDays={activeFacility?.working_days} shortages={shortages} onSelectOrder={setMaterialsOrder} focusLineId={ganttFocus} onClearFocus={() => setGanttFocus(null)}
              hideUnassigned={scope.scoped} />
          </TabsContent>

          <TabsContent value="bom" className="mt-6 space-y-6">
            <BomPanel items={bomItems} role={role} incoming={incoming} suppliers={suppliers} machines={machines} facility={facility} salesOrders={salesOrders} onSave={saveBomItem} onArchive={archiveBomItem} onAdjustStock={adjustStock} onProduce={produceToStock} onCreatePO={createPO} onCreateWorkOrder={createWorkOrder} onApprove={approveBomItem} onReject={rejectBomItem} onImported={loadCore} />
            {canManage && <InventoryLog refreshKey={bomItems} bomItems={bomItems} />}
          </TabsContent>

          <TabsContent value="work-orders" className="mt-6">
            <WorkOrdersPanel workOrders={workOrders} bomItems={bomItems} role={role} canManage={canManage} isAdmin={isAdmin} currentUsername={user.username} onApplyBomRefresh={applyBomRefresh} onAcknowledgeBom={acknowledgeBom}
              onCreate={createWorkOrder} onEditWorkOrder={editWorkOrder} onLogStage={openStage("log")} onShipStage={openStage("ship")} onReceiveStage={openStage("receive")}
              onHistory={setHistoryOrder} onDelete={deleteWorkOrder} onOpenOrder={onOpenOrder} />
          </TabsContent>

          <TabsContent value="machines" className="mt-6">
            <MachinesPanel machines={scope.machines} orders={scope.orders} role={role} downtimes={scope.downtimes} operators={operatorUsers} shiftHours={activeFacility?.shift_hours}
              onCreate={createMachine} onUpdate={updateMachine} onDelete={deleteMachine} onLogDowntime={logDowntime} onDeleteDowntime={deleteDowntime} />
          </TabsContent>

          <TabsContent value="inventory" className="mt-6">
            <GlobalInventory facility={facility} />
          </TabsContent>

          <TabsContent value="purchasing" className="mt-6">
            <PurchaseOrdersPanel suppliers={suppliers} canManage={canManage} isAdmin={isAdmin} mineOnly={false} currentEmail={user.username} facility={facility} poVersion={poVersion}
              bomItems={bomItems} onCreatePO={createBulkPO}
              onReceive={receivePO} onEdit={editPO} onCancel={cancelPO} onApprove={approvePO} onReject={rejectPO} onResubmit={resubmitPO} onBulkApprove={bulkApprove} onUpdateSupplier={updateSupplier} onImported={loadCore} />
          </TabsContent>

          {canManage && (
            <>
              <TabsContent value="suppliers" className="mt-6">
                <SuppliersPanel suppliers={suppliers} isAdmin={isAdmin} canManage={canManage} currentEmail={user.username} bomItems={bomItems} onCreatePO={createPO} onReorder={reorderPO} onCreate={createSupplier} onUpdate={updateSupplier}
                  onApprove={approveSupplier} onReject={rejectSupplier} onResubmit={resubmitSupplier} onImported={loadCore} />
              </TabsContent>
              <TabsContent value="customers" className="mt-6">
                <CustomersPanel customers={customers} isAdmin={isAdmin} canManage={canManage} currentEmail={user.username} onCreate={createCustomer} onUpdate={updateCustomer}
                  onApprove={approveCustomer} onReject={rejectCustomer} onResubmit={resubmitCustomer} onImported={loadCore} />
              </TabsContent>
              <TabsContent value="sales" className="mt-6">
                <SalesPanel salesOrders={salesOrders} orders={allStageOrders} customers={customers} bomItems={bomItems} canManage isAdmin={isAdmin} onCreate={createSalesOrder} onUpdate={updateSalesOrder} onConfirm={confirmSalesOrder}
                  onFulfill={fulfillSalesOrder} onCancel={cancelSalesOrder} onCreatePO={createPO} onRaiseAllPOs={raisePosForSalesOrder} onCreateWorkOrder={createWorkOrderForSalesOrder} onImported={loadCore}
                  onJumpToGantt={(o) => { if (o?.line_id) setGanttFocus(o.line_id); setTab(o?.kind === "work_order" ? "work-orders" : "schedule"); }} />
              </TabsContent>
              <TabsContent value="osp" className="mt-6">
                <OspPanel vendors={ospVendors} gatePasses={gatePasses} canManage onCreateVendor={createOspVendor} onReceive={receiveGatePass} onImported={loadCore} />
              </TabsContent>
              <TabsContent value="planning" className="mt-6">
                <ProductionPlanning salesOrders={salesOrders} bomItems={bomItems} orders={orders} canCreate onOrderAllShort={orderAllShort}
                  onCreateOrder={(pf) => setShortageFor({ item_id: pf.bom_item_id, name: pf.product_name, product_name: pf.product_name, quantity: pf.target_quantity })} />
              </TabsContent>
              <TabsContent value="system" className="mt-6">
                <SystemOverview bomItems={bomItems} suppliers={suppliers} customers={customers} salesOrders={salesOrders} orders={orders} machines={machines} poSummary={poSummary} onNavigate={setTab} />
              </TabsContent>
            </>
          )}

          {can.viewUsers(role) && (
            <TabsContent value="users" className="mt-6">
              <UserManagement users={users} machines={machines} currentUser={user} onCreate={createUser} onUpdate={updateUser} onDelete={deleteUser} onClearData={clearAllData} onSeedDemo={seedDemoData} onImported={loadUsers} lockoutTick={lockoutTick} onLockoutChange={refreshLockouts} />
              {isAdmin && <div className="mt-8" data-testid="trusted-devices-section"><TrustedDevices refreshTick={deviceTick} /></div>}
            </TabsContent>
          )}
        </Tabs>
        </main>
      </div>

      <OrderFormModal open={orderModal.open} onOpenChange={(v) => setOrderModal((s) => ({ ...s, open: v }))} order={orderModal.order} prefill={orderModal.prefill} machines={machines} bomItems={bomItems} onSubmit={submitOrder} />
      <MaterialsSheet order={materialsOrder} onClose={() => setMaterialsOrder(null)} onViewSalesOrder={() => { setMaterialsOrder(null); setTab("sales"); }} />
      {shortageFor && (
        <MaterialShortageDialog product={shortageFor} facility={facility}
          onClose={() => setShortageFor(null)} onCreatePO={createPO} onBulkPO={createBulkPO} onProceed={proceedFromShortage} />
      )}
      <PurchaseSuggestionsDialog open={purchaseOpen} onOpenChange={setPurchaseOpen} suggestions={purchaseSuggestions} onCreatePO={createPO} suppliers={activeSuppliers} openPoItemIds={openPoItemIds} />
      <PlantSettingsDialog open={plantSettingsOpen} onOpenChange={setPlantSettingsOpen} facility={activeFacility} onSave={savePlantSettings} />
      <DigestSettingsDialog open={digestOpen} onOpenChange={setDigestOpen} />
      {stageAction?.type === "log" && <StageLogDialog ctx={stageCtx} onClose={() => setStageAction(null)} onSubmit={logStage} />}
      {stageAction?.type === "ship" && <ShipToVendorDialog ctx={stageCtx} vendors={ospVendors} onClose={() => setStageAction(null)} onSubmit={shipStage} />}
      {stageAction?.type === "receive" && <ReceiveFromVendorDialog ctx={stageCtx} gatePasses={gatePasses} onClose={() => setStageAction(null)} onSubmit={receiveStage} />}
      <StageHistoryDrawer order={liveHistoryOrder} onClose={() => setHistoryOrder(null)} />
      <SuggestionDialog suggestion={suggestion} onClose={() => setSuggestion(null)} onApply={applySuggestion} />
      <ConfirmDialog confirm={confirm} onClose={() => setConfirm(null)} />
    </div>
  );
}
